import { call } from '@/lib/api';
import { Document } from '@/types';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  rem,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PiFloppyDisk } from 'react-icons/pi';
import classes from './DocumentEntry.module.css';

const useHiddenMutation = (pulseId: string, id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hidden: boolean) =>
      call('document/set-hidden', {
        json: { id, hidden },
      }),
    onSuccess: (_, hidden) => {
      notifications.show({
        title: `Success`,
        message: `Document is now ${hidden ? 'hidden' : 'visible'}`,
        color: 'green',
        position: 'top-left',
      });
      queryClient.setQueryData(
        ['documents', 'list', pulseId],
        (old: Document[]) =>
          old
            .map((doc) => {
              if (doc.id == id) {
                doc.hidden = !doc.hidden;
              }
              return doc;
            })
            .slice(),
      );
    },
  });
};

const useDetailsMutation = (pulseId: string, id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, filename }: { name?: string; filename?: string }) =>
      call('document/update', {
        json: { id, name, filename },
      }),
    onSuccess: (_, { name, filename }) => {
      notifications.show({
        title: `Success`,
        message: name
          ? `Document is now renamed to ${name}`
          : `Filename is changed to ${filename}`,
        color: 'green',
        position: 'top-left',
      });
      queryClient.setQueryData(
        ['documents', 'list', pulseId],
        (old: Document[]) =>
          old
            .map((doc) => {
              if (doc.id == id) {
                if (name) doc.name = name;
                if (filename) doc.filename = filename;
              }
              return doc;
            })
            .slice(),
      );
    },
  });
};

const useDeletionMutation = (pulseId: string, id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      call('document/delete', {
        json: { id },
      }),
    onSuccess: () => {
      notifications.show({
        title: `Success`,
        message: `Document is deleted`,
        position: 'top-left',
        color: 'green',
      });
      queryClient.setQueryData(
        ['documents', 'list', pulseId],
        (old: Document[]) => old.filter((doc) => doc.id !== id).slice(),
      );
    },
  });
};

function Details({
  open,
  doc,
  onClose,
  pulseId,
}: {
  doc: Document;
  open: boolean;
  onClose: () => void;
  pulseId: string;
}) {
  const [filename, setFilename] = useState(doc.filename);
  const [name, setName] = useState(doc.name);
  const visibility = useHiddenMutation(pulseId, doc.id);
  const details = useDetailsMutation(pulseId, doc.id);
  const deletion = useDeletionMutation(pulseId, doc.id);

  const confirmDeletion = () =>
    modals.openConfirmModal({
      title: 'Please confirm your action',
      children: (
        <Text size="sm">
          Please confirm that you inted to delete the folowing file: {doc.name}?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      onConfirm: () => deletion.mutate(),
    });

  return (
    <Drawer
      position="right"
      opened={open}
      onExitTransitionEnd={() => {
        setFilename(doc.filename);
        setName(doc.name);
      }}
      onClose={onClose}
    >
      <Flex
        gap="md"
        direction="column"
        justify="space-between"
        style={{ height: `calc(100vh - ${rem(80)})` }}
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            description="A name of the document, a short concise name for best UX. Client has to easly identify the document by this name."
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                details.mutate({ name });
              }
            }}
            rightSection={
              <Tooltip label="Save changes" withArrow position="top">
                <ActionIcon
                  disabled={name === doc.name}
                  variant="light"
                  onClick={() => {
                    details.mutate({ name });
                  }}
                >
                  <PiFloppyDisk size={16} />
                </ActionIcon>
              </Tooltip>
            }
          />
          <Textarea
            label="Filename"
            description={"A filename of the document once it's downloaded. "}
            value={filename}
            onChange={(event) => setFilename(event.currentTarget.value)}
            rightSectionProps={{ style: { alignSelf: 'flex-start' } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                details.mutate({ filename });
              }
            }}
            rightSection={
              <Tooltip label="Save changes" withArrow position="top">
                <ActionIcon
                  variant="light"
                  disabled={filename === doc.filename}
                  onClick={() => {
                    details.mutate({ filename, name: undefined });
                  }}
                >
                  <PiFloppyDisk size={16} />
                </ActionIcon>
              </Tooltip>
            }
          />
          <Box py={'md'}>
            <Switch
              defaultChecked
              labelPosition="left"
              onLabel="YES"
              offLabel="NO"
              checked={doc.hidden}
              onChange={(event) => {
                visibility.mutate(event.currentTarget.checked);
              }}
              label="Is document hidden from Client Portal?"
            />
          </Box>
        </Stack>
        <Flex justify="space-between" direction="column" gap="md">
          <Text size="xs" c="dimmed">
            Uploaded by {doc.createdBy} at{' '}
            {dayjs(doc.createdAt).format('HH:mm, DD.MM.YYYY')}
          </Text>
          <Flex justify="space-between">
            <Button
              onClick={() => {
                call('document/download', {
                  json: {
                    id: doc.id,
                  },
                })
                  .then((res) => res.blob())
                  .then((bytes) => {
                    const href = URL.createObjectURL(bytes);
                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                  });
              }}
            >
              Download
            </Button>
            <Button color="red" onClick={confirmDeletion}>
              Delete
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
}

export default function DocumentEntry({
  doc,
  pulseId,
}: {
  doc: Document;
  pulseId: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Details
        doc={doc}
        open={open}
        onClose={() => setOpen(false)}
        pulseId={pulseId}
      />
      <Box
        p={'xs'}
        onClick={() => setOpen(!open)}
        className={classes.entry}
        pos="relative"
      >
        <Text size="sm">{doc.name}</Text>
        {doc.hidden && (
          <Badge
            variant="light"
            pos="absolute"
            right={'var(--mantine-spacing-sm)'}
            top={'var(--mantine-spacing-sm)'}
          >
            Hidden
          </Badge>
        )}
        <Text size="xs" c="dimmed" truncate>
          Filename: {doc.filename}
        </Text>
      </Box>
    </>
  );
}
